import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  PieChart, TrendingUp, Activity, Target, 
  Calculator, X, Wrench, ArrowRight, Sparkles
} from 'lucide-react';
import BudgetSimulator from './tools/BudgetSimulator';
import FreedomCalculator from './tools/FreedomCalculator';
import HealthDiagnosis from './tools/HealthDiagnosis';
import GoalsPlanner from './tools/GoalsPlanner';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';

const tools = [
  {
    id: 'budget',
    name: 'Simulador de Orçamento',
    description: 'Distribua sua renda em categorias usando princípios bíblicos de prosperidade',
    icon: PieChart,
    accent: 'var(--accent-gold-400)',
    component: BudgetSimulator
  },
  {
    id: 'freedom',
    name: 'Calculadora de Liberdade',
    description: 'Descubra em quanto tempo você alcançará a independência financeira',
    icon: TrendingUp,
    accent: 'var(--accent-bronze-400)',
    component: FreedomCalculator
  },
  {
    id: 'health',
    name: 'Diagnóstico Financeiro',
    description: 'Avalie sua saúde financeira e receba recomendações personalizadas',
    icon: Activity,
    accent: 'var(--accent-gold-500)',
    component: HealthDiagnosis
  },
  {
    id: 'goals',
    name: 'Planejador de Metas',
    description: 'Defina suas metas e saiba exatamente quanto guardar por mês',
    icon: Target,
    accent: 'var(--accent-bronze-500)',
    component: GoalsPlanner
  }
];

const FinancialToolsSection = () => {
  const [activeTool, setActiveTool] = useState(null);
  const { theme } = useLanguage();
  const isDark = theme === 'dark';

  const handleOpenTool = (toolId) => {
    setActiveTool(toolId);
  };

  const handleCloseTool = () => {
    setActiveTool(null);
  };

  const ActiveComponent = activeTool 
    ? tools.find(t => t.id === activeTool)?.component 
    : null;

  return (
    <>
      <section 
        id="tools"
        className="py-24 md:py-32 relative overflow-hidden"
        style={{ background: isDark ? 'var(--bg-page)' : '#FAF7F2' }}
      >
        {/* Background Decorative Elements */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] opacity-10 pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, var(--accent-gold-400) 0%, transparent 70%)',
            filter: 'blur(80px)',
            transform: 'translate(30%, -30%)'
          }}
        />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] opacity-5 pointer-events-none"
          style={{ 
            background: 'radial-gradient(circle, var(--accent-bronze-400) 0%, transparent 70%)',
            filter: 'blur(60px)',
            transform: 'translate(-30%, 30%)'
          }}
        />

        <div className="container relative z-10">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 border border-gold-subtle"
              style={{ background: isDark ? 'rgba(184, 134, 11, 0.1)' : 'rgba(184, 134, 11, 0.05)' }}
            >
              <Wrench size={16} className="text-gold" />
              <span className="mono-text text-gold" style={{ fontSize: '0.75rem', letterSpacing: '0.1em' }}>
                FERRAMENTAS EXCLUSIVAS
              </span>
            </div>
            
            <h2 className="heading-hero mb-6">
              Domine suas Finanças com <br />
              <span className="gold-shimmer">Sabedoria e Propósito</span>
            </h2>
            <p className="body-large mx-auto" style={{ color: 'var(--text-secondary)', maxWidth: '640px' }}>
              Utilize nossas calculadoras inteligentes para alinhar sua vida financeira 
              com princípios bíblicos de mordomia e prosperidade.
            </p>
          </motion.div>

          {/* Tools Grid - Premium Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {tools.map((tool, index) => {
              const IconComponent = tool.icon;
              return (
                <motion.div
                  key={tool.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.15, ease: [0.16, 1, 0.3, 1] }}
                  whileHover={{ y: -8 }}
                  className="relative group cursor-pointer"
                  onClick={() => handleOpenTool(tool.id)}
                >
                  {/* Card Glow Effect on Hover */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-gold to-bronze rounded-2xl opacity-0 group-hover:opacity-30 transition duration-500 blur" />
                  
                  <div className="relative p-8 rounded-2xl border border-divider h-full flex flex-col transition-all duration-500"
                    style={{ 
                      background: isDark ? 'rgba(30, 30, 30, 0.4)' : 'rgba(255, 255, 255, 0.7)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <div className="flex items-start gap-6">
                      <div 
                        className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-500 group-hover:scale-110 shadow-lg"
                        style={{ 
                          background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.8)',
                          border: `1px solid ${isDark ? 'rgba(184, 134, 11, 0.3)' : 'rgba(184, 134, 11, 0.1)'}`
                        }}
                      >
                        <IconComponent size={32} style={{ color: 'var(--accent-gold-400)' }} />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-bold font-serif" style={{ color: 'var(--text-primary)' }}>
                            {tool.name}
                          </h3>
                          <Sparkles size={14} className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        </div>
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                          {tool.description}
                        </p>
                      </div>
                    </div>

                    <div className="mt-8 flex items-center justify-between pt-6 border-t border-divider">
                      <div className="flex items-center gap-1.5 text-gold font-medium text-sm">
                        <span>Acessar agora</span>
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                      <div className="text-[0.65rem] mono-text opacity-40 uppercase tracking-widest">
                        Gratuito
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-20 p-8 rounded-3xl text-center relative overflow-hidden max-w-3xl mx-auto"
            style={{ 
              background: isDark ? 'rgba(184, 134, 11, 0.05)' : 'rgba(184, 134, 11, 0.03)',
              border: '1px dashed var(--accent-gold-400)'
            }}
          >
            <Calculator size={24} className="mx-auto mb-4 text-gold opacity-60" />
            <h4 className="text-lg font-bold mb-2">Precisa de um acompanhamento direto?</h4>
            <p className="body-small mb-6 text-muted">
              Nossas ferramentas são poderosas, mas nada substitui um mentor focado no seu resultado específico.
            </p>
            <motion.a
              href="#contact"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="btn-primary inline-flex items-center gap-2"
            >
              Falar com Lucimar no WhatsApp
              <ArrowRight size={18} />
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Modern Tool Modal Overlay */}
      <AnimatePresence>
        {activeTool && ActiveComponent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
            style={{ backdropFilter: 'blur(10px)', background: 'rgba(0, 0, 0, 0.8)' }}
            onClick={handleCloseTool}
          >
            <motion.div
              initial={{ y: 50, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 50, opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="w-full max-w-4xl max-h-[90vh] rounded-3xl overflow-hidden shadow-2xl relative flex flex-col"
              style={{ 
                background: isDark ? '#141414' : '#FFFFFF',
                border: '1px solid rgba(184, 134, 11, 0.2)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-6 md:p-8 border-b border-divider flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gold-subtle flex items-center justify-center text-gold">
                    {React.createElement(tools.find(t => t.id === activeTool).icon, { size: 24 })}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold font-serif">{tools.find(t => t.id === activeTool).name}</h3>
                    <p className="text-xs text-muted uppercase tracking-widest">Ferramenta Inteligente</p>
                  </div>
                </div>
                <button 
                  onClick={handleCloseTool}
                  className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-gold-subtle text-muted hover:text-gold transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-6 md:p-8">
                <div className="max-w-3xl mx-auto">
                  <ActiveComponent onComplete={handleCloseTool} />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FinancialToolsSection;
